/*
  This file is for composing the GotS interface. Its purpose is to lay out the
  structure in a responsive way. It should not maintain state or do network
  things.
*/

import React, {
  RefObject, useRef, useState, useCallback, useEffect,
} from 'react'
import { ChatInput } from '../OracleCommons/ChatInput'
import {
  OracleThemeProvider,
  OracleScrollIndicator,
  ModerationError,
} from '../OracleCommons/index'
import { OracleErrorToast } from '../OracleCommons/OracleErrorToast'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import { OracleGuideOnSideHeader } from './OracleGuideOnSideHeader'
import { OracleGuideOnSideContent } from './OracleGuideOnSideContent'
import { type GaiaDocument } from '../OracleCommons/gaiaTagParser'
import type { ConceptMoment } from './hooks/useConceptMoments'
import { OracleGuideOnSideVideoPrompts } from './OracleGuideOnSideVideoPrompts'

/**
 * Props interface showing what data flows from root → layout → children
 *
 * Layout receives chat state from root (which has the hook) and passes it down
 */
export interface OracleGuideOnSideLayoutProps {
  // Existing visibility props
  metaVisible?: boolean
  controlsVisible?: boolean

  // Chat state props (streaming and otherwise)
  chatSessionId?: string
  streamingText?: string
  isStreaming?: boolean
  pendingMessage?: string
  sendMessage?: (message: string) => void
  stopStreaming?: () => void

  // Container focus callbacks for managing video player auto-hide
  // These are attached to the container div so that focus moving between
  // elements within the chat area (e.g., textarea to buttons) doesn't
  // trigger the video player's auto-hide behavior
  onContainerFocus?: () => void
  onContainerBlur?: () => void
  containerRef?: RefObject<HTMLDivElement | null>

  // Close callback - triggered when user presses Escape
  onClose?: () => void

  // Scroll indicator props
  showScrollIndicator?: boolean
  onScrollToBottom?: () => void
  onScrollIndicatorChange?: (visible: boolean, scrollToBottom: () => void) => void

  // Error toast props
  showErrorToast?: boolean
  errorMessage?: string
  onErrorToastClose?: () => void

  // Outer container ref - for mouse activity tracking in video.js plugin
  // Accepts callback ref (from useState setter) or RefObject
  outerContainerRef?: ((el: HTMLDivElement | null) => void) | RefObject<HTMLDivElement | null>

  // Moderation error from content policy violation
  moderationError?: ModerationError | null

  // Video.js player reference - for play/pause button in portrait mobile
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  player?: any

  // Documents from current streaming response (for Gaia tag resolution)
  streamingDocuments?: GaiaDocument[] | null

  // Active concept moment (for moment-scoped prompts when guide is open)
  activeMoment?: ConceptMoment | null

  // When true, skip rendering the drag handle (AppDrawer provides its own)
  hideDragHandle?: boolean

  // When true, ChatInput renders a tap target + overlay instead of inline textarea (iOS)
  useOverlay?: boolean
}

/**
 * Custom hook for bottom sheet drag interaction in portrait mobile.
 * Supports drag down to close/collapse and drag up to expand.
 *
 * States:
 *  - 'half'  = default open (fills space below video)
 *  - 'full'  = expanded (nearly full screen)
 *
 * Drag gestures:
 *  - Drag down from 'full'  -> collapse to 'half'
 *  - Drag down from 'half'  -> close (via onClose callback)
 *  - Drag up   from 'half'  -> expand to 'full'
 *
 * The 'vjs-oracle-guide-expanded' CSS class is toggled on the video.js
 * player element to control the expanded height via SCSS.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useBottomSheetDrag = (onClose?: () => void, player?: any) => {
  const [sheetState, setSheetState] = useState<'half' | 'full'>('half')
  const startY = useRef(0)
  const currentY = useRef(0)
  const sheetRef = useRef<HTMLDivElement>(null)

  // Sync the vjs-oracle-guide-expanded class with sheetState
  useEffect(() => {
    if (!player) return
    if (sheetState === 'full') {
      player.addClass('vjs-oracle-guide-expanded')
    } else {
      player.removeClass('vjs-oracle-guide-expanded')
    }
  }, [sheetState, player])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (!e.touches.length) return
    startY.current = e.touches[0].clientY
    currentY.current = e.touches[0].clientY
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!e.touches.length) return
    currentY.current = e.touches[0].clientY
    const delta = currentY.current - startY.current
    if (sheetRef.current && delta > 0) {
      // Only allow visual drag downward
      sheetRef.current.style.transform = `translateY(${delta}px)`
    }
  }, [])

  const handleTouchEnd = useCallback(() => {
    const delta = currentY.current - startY.current
    if (delta > 100) {
      if (sheetState === 'full') {
        // Dragged down from full -> collapse to half
        setSheetState('half')
      } else if (onClose) {
        // Dragged down from half -> close
        onClose()
      }
    } else if (delta < -100 && sheetState === 'half') {
      // Dragged up from half -> expand to full
      setSheetState('full')
    }
    // Reset transform
    if (sheetRef.current) {
      sheetRef.current.style.transform = ''
    }
  }, [onClose, sheetState])

  return {
    sheetRef, sheetState, handleTouchStart, handleTouchMove, handleTouchEnd,
  }
}

export const OracleGuideOnSideLayout: React.FC<OracleGuideOnSideLayoutProps> = ({
  // Visibility props - only used by legacy (non-plugin) implementation.
  // When rendered inside the video.js plugin, visibility is handled by CSS
  // via .vjs-user-active class, so these are not needed.
  metaVisible,
  controlsVisible,
  // Chat state props
  chatSessionId,
  streamingText,
  isStreaming,
  pendingMessage,
  sendMessage,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  stopStreaming, // TODO: implement this. User presses Escape or the Stop button
  // Container focus callbacks
  onContainerFocus,
  onContainerBlur,
  containerRef,
  onClose,
  // Scroll indicator
  showScrollIndicator,
  onScrollToBottom,
  onScrollIndicatorChange,
  // Error toast
  showErrorToast,
  errorMessage,
  onErrorToastClose,
  outerContainerRef,
  // Moderation error
  moderationError,
  // Player reference for portrait mobile play/pause
  player,
  // Documents from streaming response
  streamingDocuments,
  // Active concept moment for moment-scoped prompts
  activeMoment,
  // When true, skip rendering drag handle (AppDrawer provides its own)
  hideDragHandle,
  // iOS overlay mode for ChatInput
  useOverlay,
}) => {
  const inputPlaceholder = useTranslatedText({ keys: ['gots', 'inputPlaceholder'] })
  const disclaimer = useTranslatedText({ keys: ['gots', 'disclaimer'] })
  const {
    sheetRef, sheetState, handleTouchStart, handleTouchMove, handleTouchEnd,
  } = useBottomSheetDrag(onClose, player)

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && onClose) {
      e.preventDefault()
      onClose()
    }
  }

  // Build className only if visibility props are provided (legacy mode)
  // When inside video.js plugin, these are undefined and CSS handles visibility
  const classNames: string[] = []
  if (metaVisible !== undefined && metaVisible) {
    classNames.push('oracle-guide-on-side--visible')
  }
  if (controlsVisible !== undefined && controlsVisible) {
    classNames.push('oracle-guide-on-side--controls-visible')
  }
  const className = classNames.join(' ')

  // Layout className includes sheet state for portrait mode
  const layoutClassNames = ['oracle-guide-on-side-layout']
  if (sheetState === 'full') {
    layoutClassNames.push('oracle-guide-on-side-layout--full')
  }

  return (
    <OracleThemeProvider forcedTheme="dark" containerClass="oracle-gots-container">
      <div
        id="oracle-guide-on-side"
        className={className}
        ref={(el) => {
          // Merge outerContainerRef and sheetRef
          if (typeof outerContainerRef === 'function') {
            outerContainerRef(el)
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const ref = sheetRef as any
          ref.current = el
        }}
      >
        {/* Drag handle - visible only in portrait mobile via CSS */}
        {!hideDragHandle && (
          <div
            className="oracle-guide-on-side-layout__drag-handle"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
        )}
        <div
          className={layoutClassNames.join(' ')}
          ref={containerRef}
          onFocus={onContainerFocus}
          onBlur={onContainerBlur}
          onKeyDown={handleKeyDown}
        >
          <OracleGuideOnSideHeader onClose={onClose} player={player} />

          {/* Content area - receives streaming state */}
          <OracleGuideOnSideContent
            chatSessionId={chatSessionId}
            isStreaming={isStreaming}
            streamingText={streamingText}
            pendingMessage={pendingMessage}
            onScrollIndicatorChange={onScrollIndicatorChange}
            moderationError={moderationError}
            streamingDocuments={streamingDocuments}
          />

          {/* Prompts area with scroll indicator - wrapper always renders */}
          <div className="oracle-guide-on-side-layout__prompts-wrapper">
            <OracleScrollIndicator
              isVisible={showScrollIndicator ?? false}
              onClick={onScrollToBottom ?? (() => {})}
            />
            <OracleGuideOnSideVideoPrompts
              isStreaming={isStreaming}
              sendMessage={sendMessage}
              activeMoment={activeMoment}
            />
          </div>

          {/* Input area - receives sendMessage callback */}
          <div className="oracle-guide-on-side-layout__input-container">
            <ChatInput
              onSendMessage={sendMessage || (() => { })}
              disabled={isStreaming}
              placeholder={inputPlaceholder}
              useOverlay={useOverlay}
              disclaimer={disclaimer}
            />
            <span className="oracle-guide-on-side-layout__disclaimer">
              {disclaimer}
            </span>
          </div>
        </div>
      </div>
      <OracleErrorToast
        isVisible={showErrorToast ?? false}
        onClose={onErrorToastClose ?? (() => {})}
        message={errorMessage}
      />
    </OracleThemeProvider>
  )
}
